import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AnimalKind, Gender, IStemAnimal } from 'shared/api/api';
import { CheckIfSectionUsesPens } from 'shared/helpers/location-helper';
import { getYoungAnimals } from 'shared/helpers/stemanimal-helper/stem-animal-input-helper';
import { getMaxTransponderNumber } from 'shared/helpers/stemanimal-helper/stemanimal-helper';
import { WebAppState } from 'web/state/store.web';
import { SkioldFormsWrapper } from 'web/view/components/skiold-components/skiold-forms-wrapper/skiold-forms-wrapper';
import { FormRow } from 'web/view/components/skiold-components/skiold-forms-wrapper/skiold-forms-wrapper-types';
import { SkioldModal } from 'web/view/components/skiold-components/skiold-modal/skiold-modal';
import { ViewWeb } from 'web/view/components/utils/web-view';
import SearchYoungAnimals from '../search-animals/search-young-animals';
import {
	getAgeInWeeksRow,
	getDateRow,
	getDeviationPeriodInputRow,
	getFeedCurvePickerRow,
	getFixedDeviationInputRow,
	getGenderRow,
	getIdNumberRow,
	getLocationRow,
	getPenPickerRow,
	getStableSectionPickerRow,
	getTempFeedDeviationInputRow,
	getTransponderRow,
} from './move-young-animal-event-row-helpers';
import './register-move-young-animal-event.scss';
import { getSectionSelectionUpdate } from 'shared/helpers/move-young-animal-helper/move-young-animal-event-helpers';
import { useYoungAnimalMoveEvent } from 'shared/helpers/move-young-animal-helper/move-young-animal-provider';

export interface Props {}

export const RegisterYoungAnimalMoveForm: FC<Props> = memo(() => {
	const [isModalOpen, setModal] = useState<boolean>(false);
	const [sectionHasStation, setSectionHasStation] = useState<boolean>(false);

	const processEquipmentData = useSelector((state: WebAppState) => state.processEquipmentData).entities;
	const showDeviationFixed = useSelector(
		(state: WebAppState) => state.generalSettings.entity.elistarEsfAdjustFeedFixedDeviation,
	);

	const { state, updateField } = useYoungAnimalMoveEvent();

	const closeYoungAnimalModal = useCallback(() => {
		setModal(false);
	}, []);

	const openYoungAnimalModal = useCallback(() => {
		setModal(true);
	}, []);

	const selectYoungAnimal = useCallback(
		(youngAnimal: IStemAnimal | undefined) => {
			updateField('youngAnimal', {
				...youngAnimal,
				transponder: youngAnimal?.transponder,
			});
			updateField('transponderNr', youngAnimal?.transponder);
			closeYoungAnimalModal();
		},
		[closeYoungAnimalModal, updateField],
	);

	const transponderNrChanged = useCallback(
		(transponderNr: number | undefined) => {
			if (!transponderNr || transponderNr <= getMaxTransponderNumber(processEquipmentData)) {
				updateField('transponderNr', transponderNr);
				updateField('youngAnimal', {
					...state.youngAnimal,
					transponder: transponderNr?.toString(),
				});
			}
		},
		[processEquipmentData, state.youngAnimal, updateField],
	);

	const selectSection = useCallback(
		(selectedSectionId: string) => {
			const { sectionId, penId, sectionHasStation } = getSectionSelectionUpdate(
				selectedSectionId,
				processEquipmentData,
				state.youngAnimal?.entrancePenId,
			);
			updateField('sectionId', sectionId);
			updateField('penId', penId);
			setSectionHasStation(sectionHasStation);
		},
		[processEquipmentData, state.youngAnimal?.entrancePenId, updateField],
	);

	const dateRow = useMemo(() => {
		const now = new Date();

		return getDateRow(state.date ?? now, undefined, (date: Date) => updateField('date', date));
	}, [state.date, updateField]);

	const stablePickerRow = useMemo(() => {
		return getStableSectionPickerRow(state.sectionId, state.penId, selectSection);
	}, [selectSection, state.penId, state.sectionId]);

	const genderRow = useMemo(() => {
		return getGenderRow(Gender.Female, gender => updateField('gender', gender));
	}, [updateField]);

	const penPickerRow = useMemo(() => {
		return getPenPickerRow((penId?: string) => updateField('penId', penId), state.sectionId, state.youngAnimal);
	}, [state.sectionId, state.youngAnimal, updateField]);

	const feedCurvePickerRow = useMemo(() => {
		return getFeedCurvePickerRow(
			state.feedCurveNumber ?? 0,
			state.penId,
			feedCurveNumber => updateField('feedCurveNumber', feedCurveNumber),
			AnimalKind.YoungAnimal,
		);
	}, [state.feedCurveNumber, state.penId, updateField]);

	const fixedDeviationInputRow = useMemo(() => {
		return getFixedDeviationInputRow(
			fixedDeviation => updateField('fixedDeviation', fixedDeviation),
			state.fixedDeviation,
		);
	}, [state.fixedDeviation, updateField]);

	const deviationPeriodInputRow = useMemo(() => {
		return getDeviationPeriodInputRow(
			deviationPeriod => updateField('deviationPeriod', deviationPeriod),
			state.deviationPeriod,
		);
	}, [state.deviationPeriod, updateField]);
	const tempFeedDeviationInputRow = useMemo(() => {
		return getTempFeedDeviationInputRow(
			tempFeedDeviation => updateField('tempFeedDeviation', tempFeedDeviation),
			state.tempFeedDeviation,
		);
	}, [state.tempFeedDeviation, updateField]);

	const idNumberRow = useMemo(() => {
		return getIdNumberRow(
			'entranceTypeFromYoungAnimal',
			idNumber => updateField('idNumber', idNumber),
			closeYoungAnimalModal,
			openYoungAnimalModal,
			state.youngAnimal?.idNumber,
		);
	}, [closeYoungAnimalModal, openYoungAnimalModal, state.youngAnimal?.idNumber, updateField]);

	const transponderRow = useMemo(() => {
		return getTransponderRow(transponderNrChanged, state.transponderNr);
	}, [state.transponderNr, transponderNrChanged]);

	const ageInWeeksRow = useMemo(() => {
		return getAgeInWeeksRow(state.youngAnimal?.birthDate);
	}, [state.youngAnimal]);

	const currentLocationRow = useMemo(() => {
		return getLocationRow(state.youngAnimal);
	}, [state.youngAnimal]);

	const formRows = useMemo(() => {
		const rows: FormRow[] = [];

		rows.push(dateRow);
		// rows.push(genderRow);
		rows.push(idNumberRow);
		rows.push(ageInWeeksRow);
		rows.push(currentLocationRow);
		rows.push(transponderRow);
		rows.push(stablePickerRow);

		if (state.sectionId && CheckIfSectionUsesPens(state.sectionId)) {
			rows.push(penPickerRow);
		}

		if (sectionHasStation) {
			rows.push(feedCurvePickerRow);
		}

		if (sectionHasStation && showDeviationFixed) {
			rows.push(fixedDeviationInputRow);
		}

		if (sectionHasStation && showDeviationFixed) {
			rows.push(tempFeedDeviationInputRow);
		}
		if (sectionHasStation && showDeviationFixed) {
			rows.push(deviationPeriodInputRow);
		}

		return rows;
	}, [
		ageInWeeksRow,
		currentLocationRow,
		dateRow,
		deviationPeriodInputRow,
		feedCurvePickerRow,
		fixedDeviationInputRow,
		idNumberRow,
		penPickerRow,
		sectionHasStation,
		showDeviationFixed,
		stablePickerRow,
		state.sectionId,
		tempFeedDeviationInputRow,
		transponderRow,
	]);

	return (
		<ViewWeb className="register-move-young-animal-event">
			<SkioldFormsWrapper formRows={formRows} containerClassName="move-event-forms-wrapper" />

			<SkioldModal isOpen={isModalOpen} close={() => closeYoungAnimalModal()}>
				<SearchYoungAnimals
					close={youngAnimal => selectYoungAnimal(youngAnimal)}
					youngAnimals={getYoungAnimals()}
				/>
			</SkioldModal>
		</ViewWeb>
	);
});
